import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, Trophy, Phone, Mail, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { brandStyle } from "@/lib/brand";
import { STYLE_QUESTIONS, scoreStyle } from "@/lib/disc";
import { StyleMeter } from "@/components/StyleMeter";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { CommentField } from "@/components/CommentField";
import { saveUnlock } from "@/lib/unlock";

const QUESTIONS = [
  {
    key: "budget",
    q: "What's your budget?",
    options: ["Under $25k", "$25k – $40k", "$40k – $60k", "$60k+ / flexible"],
  },
  {
    key: "category",
    q: "What are you shopping for?",
    options: ["Sedan", "SUV", "Truck", "EV / Electric", "Performance / Sports"],
  },
  {
    key: "financing",
    q: "How are you planning to pay?",
    options: ["Financing", "Lease", "Cash", "Not sure yet"],
  },
  {
    key: "timeline",
    q: "When are you looking to buy?",
    options: ["Today / this week", "This month", "1–3 months", "Just researching"],
  },
  ...STYLE_QUESTIONS.map((sq, i) => ({
    key: sq.key,
    q: sq.q,
    section: "Quick style check",
    sub: i === 0 ? "4 quick picks — this is how we pair you with a specialist who works the way you do" : "Pick the one most like you",
    options: sq.options.map((o) => o.t),
  })),
];

const NQ = QUESTIONS.length;
const S = { NOTE: NQ, CAPTURE: NQ + 1, RESULT: NQ + 2, DONE: NQ + 3 };

export default function SpecialistMatcher({ open, onOpenChange, vehicle, dealer }) {
  const [step, setStep] = useState(0); // 0..NQ-1 quiz, then note, result, contact, done
  const [answers, setAnswers] = useState({});
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [lead, setLead] = useState(null);

  const reset = () => {
    setStep(0);
    setAnswers({});
    setNote("");
    setResult(null);
    setLead(null);
    setForm({ customer_name: "", phone: "", email: "" });
  };

  const handleClose = (o) => {
    onOpenChange(o);
    if (!o) setTimeout(reset, 300);
  };

  const pick = (key, val) => {
    const q = QUESTIONS[step];
    if (q.multi) {
      setAnswers((a) => {
        const cur = a[key] || [];
        const next = cur.includes(val)
          ? cur.filter((x) => x !== val)
          : cur.length >= (q.max || 2)
            ? cur
            : [...cur, val];
        return { ...a, [key]: next };
      });
      return;
    }
    setAnswers((a) => ({ ...a, [key]: val }));
    setTimeout(() => setStep((s) => s + 1), 180);
  };

  const captureAndMatch = async (e) => {
    e.preventDefault();
    if (!form.phone.trim() && !form.email.trim()) {
      toast.error("Add a phone number or email so your specialist can reach you");
      return;
    }
    setLoading(true);
    setStep(S.RESULT);
    const customer_style = scoreStyle(answers);
    try {
      const { data } = await api.post("/public/match", {
        vehicle_id: vehicle?.id,
        dealer_id: dealer?.id,
        answers,
        customer_style,
        note,
      });
      setResult(data);
      const top = data?.top;
      const { data: created } = await api.post("/public/leads", {
        vehicle_id: vehicle?.id,
        dealer_id: dealer?.id,
        source: "matcher",
        customer_name: form.customer_name,
        phone: form.phone,
        email: form.email,
        message: note,
        comment: note,
        matched_specialist_id: top?.specialist_id,
        match_reason: top?.reason,
        quiz_answers: answers,
        customer_style,
      });
      if (dealer?.id) saveUnlock(dealer.id, created);
      setLead(created);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setStep(S.CAPTURE);
    } finally {
      setLoading(false);
    }
  };

  const requestContact = async () => {
    setSubmitting(true);
    try {
      if (lead?.id) await api.post(`/public/leads/${lead.id}/contact-request`, {});
      setStep(S.DONE);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const progress = Math.min((step / S.RESULT) * 100, 100);
  const top = result?.top;
  const topSpecialist = top?.specialist;
  const currentQ = step < NQ ? QUESTIONS[step] : null;
  const isOptionActive = (q, opt) =>
    q.multi ? (answers[q.key] || []).includes(opt) : answers[q.key] === opt;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="specialist-matcher-dialog"
        style={brandStyle(dealer?.accent_color)}
        className="bg-[#0F111A] border-[#2A2E3D] text-white max-w-lg p-0 overflow-hidden gap-0"
      >
        <div className="tracing-top" />
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="font-heading text-xl font-extrabold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-brand" /> Product Specialist Matcher
          </DialogTitle>
          <DialogDescription className="sr-only">Answer a few quick questions to be matched with the right sales specialist.</DialogDescription>
        </DialogHeader>

        {step < S.RESULT && (
          <div className="px-6">
            <Progress value={progress} className="h-1.5 bg-slate-800 [&>div]:bg-brand" />
          </div>
        )}

        <div className="px-6 py-6 min-h-[300px]">
          <AnimatePresence mode="wait">
            {/* Quiz steps */}
            {currentQ && (
              <motion.div
                key={`q-${step}`}
                data-testid={`specialist-quiz-step-${step + 1}`}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.25 }}
              >
                <p className="font-mono-tech text-xs uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
                  Question {step + 1} of {NQ}
                  {currentQ.section && (
                    <span data-testid="quiz-section-badge" className="normal-case tracking-normal text-[10px] px-2 py-0.5 rounded-full bg-brand/15 text-brand-soft border border-brand/30">
                      {currentQ.section}
                    </span>
                  )}
                </p>
                <h3 className="font-heading text-2xl font-bold mb-1">{currentQ.q}</h3>
                {currentQ.sub && <p className="text-xs text-slate-500 mb-4">{currentQ.sub}</p>}
                <div className={`grid gap-2.5 ${currentQ.multi ? "sm:grid-cols-2 mt-4" : ""}`}>
                  {currentQ.options.map((opt) => {
                    const active = isOptionActive(currentQ, opt);
                    return (
                      <button
                        key={opt}
                        data-testid={`quiz-option-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        onClick={() => pick(currentQ.key, opt)}
                        className={`text-left px-4 py-3.5 rounded-xl border transition-all ${currentQ.multi ? "text-sm" : ""} ${
                          active
                            ? "border-brand bg-brand/10 shadow-[0_0_18px_rgb(var(--brand-rgb)/0.2)]"
                            : "border-slate-800 bg-slate-950 hover:border-slate-600 hover:bg-slate-900"
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between mt-4">
                  {step > 0 ? (
                    <Button
                      variant="ghost"
                      onClick={() => setStep((s) => s - 1)}
                      className="text-slate-400 hover:text-white px-0"
                    >
                      <ArrowLeft className="mr-1 h-4 w-4" /> Back
                    </Button>
                  ) : (
                    <span />
                  )}
                  {currentQ.multi && (
                    <Button
                      onClick={() => setStep((s) => s + 1)}
                      disabled={!(answers[currentQ.key] || []).length}
                      data-testid="quiz-next-btn"
                      className="bg-brand hover:bg-brand/90 font-bold"
                    >
                      Next <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  )}
                </div>
              </motion.div>
            )}

            {/* Optional note */}
            {step === S.NOTE && (
              <motion.div
                key="note"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
              >
                <h3 className="font-heading text-2xl font-bold mb-2">Anything else?</h3>
                <p className="text-sm text-slate-400 mb-4">
                  Tell us a bit about yourself so we match you perfectly. (optional)
                </p>
                <CommentField
                  label="Your note"
                  testId="matcher-note"
                  value={note}
                  onChange={setNote}
                  placeholder="e.g. First-time buyer, need great fuel economy for a long commute, prefer Spanish-speaking rep…"
                />
                <div className="flex gap-3 mt-5">
                  <Button
                    variant="ghost"
                    onClick={() => setStep(NQ - 1)}
                    className="text-slate-400 hover:text-white"
                  >
                    <ArrowLeft className="mr-1 h-4 w-4" /> Back
                  </Button>
                  <Button
                    onClick={() => setStep(S.CAPTURE)}
                    data-testid="matcher-find-btn"
                    className="flex-1 h-11 bg-brand hover:bg-brand/90 font-bold"
                  >
                    Continue <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Contact capture — unlocks the match */}
            {step === S.CAPTURE && (
              <motion.form
                key="capture"
                onSubmit={captureAndMatch}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                data-testid="lead-capture-step"
              >
                <h3 className="font-heading text-2xl font-bold mb-1">Almost there</h3>
                <p className="text-sm text-slate-400 mb-5">
                  Your best-fit specialist is one tap away. Where can {dealer?.name || "the dealership"} reach you
                  {vehicle ? ` about the ${vehicle.year} ${vehicle.model}` : ""}?
                </p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-slate-300 mb-1.5 block">Your name</Label>
                    <Input
                      required
                      data-testid="lead-name-input"
                      value={form.customer_name}
                      onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                      className="bg-slate-950 border-slate-800 focus:border-brand text-slate-100"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-300 mb-1.5 block">Mobile</Label>
                      <Input
                        type="tel"
                        data-testid="lead-phone-input"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="(555) 555-0100"
                        className="bg-slate-950 border-slate-800 focus:border-brand text-slate-100"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 mb-1.5 block">Email</Label>
                      <Input
                        type="email"
                        data-testid="lead-email-input"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="you@email.com"
                        className="bg-slate-950 border-slate-800 focus:border-brand text-slate-100"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500">Phone or email — at least one. We only share this with {dealer?.name || "the dealership"}.</p>
                </div>
                <div className="flex gap-3 mt-5">
                  <Button type="button" variant="ghost" onClick={() => setStep(S.NOTE)} className="text-slate-400 hover:text-white">
                    <ArrowLeft className="mr-1 h-4 w-4" /> Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={!form.customer_name.trim() || (!form.phone.trim() && !form.email.trim())}
                    data-testid="lead-submit-btn"
                    className="flex-1 h-11 bg-brand hover:bg-brand/90 font-bold"
                  >
                    Show my match <Sparkles className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </motion.form>
            )}

            {/* Result */}
            {step === S.RESULT && (
              <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-14 text-center">
                    <Loader2 className="h-10 w-10 animate-spin text-brand mb-4" />
                    <p className="text-slate-300 font-medium">Analyzing your answers…</p>
                    <p className="text-xs text-slate-500 mt-1">Matching you with the right specialist</p>
                  </div>
                ) : topSpecialist ? (
                  <div data-testid="match-result">
                    <div className="flex items-center gap-2 text-brand text-xs font-mono-tech uppercase tracking-widest mb-3">
                      <Trophy className="h-4 w-4" /> Your best match · {top.score}%
                    </div>
                    {result.customer_style && (
                      <p className="text-xs text-slate-400 mb-3" data-testid="customer-style-label">
                        Your style: <span className="font-semibold text-slate-200">{result.customer_style.primary}</span>
                        <span className="text-slate-500"> / {result.customer_style.secondary}</span>
                      </p>
                    )}
                    <div
                      data-testid={`associate-card-${topSpecialist.name.split(" ")[0].toLowerCase()}`}
                      className="rounded-2xl border border-brand/40 bg-gradient-to-b from-brand/10 to-transparent p-5"
                    >
                      <div className="flex items-center gap-4">
                        <img
                          src={topSpecialist.photo_url}
                          alt={topSpecialist.name}
                          className="h-16 w-16 rounded-full object-cover border-2 border-brand"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-heading text-lg font-bold">{topSpecialist.name}</div>
                          <div className="text-xs text-slate-400">{topSpecialist.title}</div>
                        </div>
                        <StyleMeter style={topSpecialist.style} showLabel={false} className="w-20 shrink-0" testId="match-style-meter" />
                      </div>
                      <p className="text-sm text-slate-300 mt-4 leading-relaxed">{top.reason}</p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {(topSpecialist.specialties || []).slice(0, 4).map((s) => (
                          <span key={s} className="text-[10px] px-2 py-1 rounded-full bg-slate-900 border border-slate-800 text-slate-400">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>

                    {result.matches?.length > 1 && (
                      <div className="mt-4">
                        <p className="text-xs text-slate-500 mb-2">Also a great fit</p>
                        <div className="space-y-2">
                          {result.matches.slice(1, 3).map((m) => (
                            <div key={m.specialist_id} className="flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-950 px-3 py-2">
                              <img src={m.specialist?.photo_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium truncate">{m.name}</div>
                                <div className="text-[11px] text-slate-500 truncate">{m.specialist?.title}</div>
                              </div>
                              <span className="text-xs text-slate-400 font-mono-tech">{m.score}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-slate-500 mt-4 text-center">
                      We've passed your details to {dealer?.name || "the dealership"}. Want {topSpecialist.name.split(" ")[0]} to call you?
                    </p>
                    <Button
                      onClick={requestContact}
                      disabled={submitting}
                      data-testid="request-contact-btn"
                      className="w-full h-11 mt-3 bg-brand hover:bg-brand/90 font-bold"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Yes, have ${topSpecialist.name.split(" ")[0]} call me`}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => handleClose(false)}
                      data-testid="maybe-later-btn"
                      className="w-full mt-1 text-slate-500 hover:text-white"
                    >
                      Maybe later
                    </Button>
                  </div>
                ) : (
                  <p className="text-slate-400 text-center py-10">No match found.</p>
                )}
              </motion.div>
            )}

            {/* Done */}
            {step === S.DONE && (
              <motion.div
                key="done"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-10"
                data-testid="lead-success"
              >
                <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto mb-4" />
                <h3 className="font-heading text-2xl font-bold">You're all set!</h3>
                <p className="text-slate-400 mt-2 max-w-xs mx-auto">
                  {topSpecialist?.name} from {dealer?.name} will be in touch shortly.
                </p>
                <div className="flex flex-col gap-2 mt-6 text-sm">
                  {topSpecialist?.phone && (
                    <a href={`tel:${topSpecialist.phone}`} className="flex items-center justify-center gap-2 text-brand-soft">
                      <Phone className="h-4 w-4" /> {topSpecialist.phone}
                    </a>
                  )}
                  {topSpecialist?.email && (
                    <a href={`mailto:${topSpecialist.email}`} className="flex items-center justify-center gap-2 text-slate-400">
                      <Mail className="h-4 w-4" /> {topSpecialist.email}
                    </a>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
